import { Transition } from '@headlessui/react'
import {
  CaretLeftIcon,
  CaretRightIcon,
  ListIcon,
  MagnifyingGlassIcon,
  XIcon,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useState } from 'react'
import { NavigationLink } from '@/src/@types/statamic'
import Button from '@/src/components/core/Button'
import { useSearchDialog } from '@/src/components/flch/components/SearchDialog'

export default function MobileNav({
  scrolled = false,
  mainNavigation,
}: {
  scrolled?: boolean
  mainNavigation: NavigationLink[]
}) {
  const router = useRouter()

  const openSearch = useSearchDialog()

  const [showNavigation, setShowNavigation] = useState(false)
  const [selectedSubNavigation, setSelectedSubNavigation] =
    useState<NavigationLink | null>(null)

  const toggleNavigation = () => {
    setShowNavigation(!showNavigation)
    setSelectedSubNavigation(null)
  }

  const closeNavigation = () => {
    setShowNavigation(false)
    setSelectedSubNavigation(null)
  }

  useEffect(() => {
    if (showNavigation) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [showNavigation])

  useEffect(() => {
    router.events.on('routeChangeComplete', closeNavigation)

    return () => router.events.off('routeChangeComplete', closeNavigation)
  }, [router.events])

  const selectedNavIndex = selectedSubNavigation
    ? mainNavigation.indexOf(selectedSubNavigation)
    : -1

  return (
    <div
      className={clsx([
        'fixed flex flex-col transition-colors',
        {
          'bg-white': scrolled || showNavigation,
          'inset-0': showNavigation,
          'inset-x-0 top-0': !showNavigation,
        },
      ])}
    >
      <div
        className={clsx([
          'z-20 w-full',
          {
            'border-b border-gray-150': scrolled || showNavigation,
          },
        ])}
      >
        {selectedSubNavigation === null && (
          <div className="flex h-15 items-center justify-between pr-2 pl-5">
            <Link href="/" className="block">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="171"
                height="21"
                fill="none"
                className="drop-shadow-xs"
              >
                <path
                  fill="#d97706"
                  d="M66 7a4 4 0 0 1 4-4h8a4 4 0 0 1 2 .536V0h6v3h6a4 4 0 0 1 3 1.355A4 4 0 0 1 98 3h8a4 4 0 0 1 3 1.355A4 4 0 0 1 112 3h8a4 4 0 0 1 3 1.355A4 4 0 0 1 126 3h8a4 4 0 0 1 4 4v6h-7v8h-20a3 3 0 0 1-3-3v-5h-5v8H61a4 4 0 0 1-4-4v-4h-5v8H18a3 3 0 0 1-3-3v-5h-4v8H1v-8H0V7h1a4 4 0 0 1 4-4h8.5c1.066 0 2.033.417 2.75 1.097A3.99 3.99 0 0 1 19 3h8a4 4 0 0 1 2 .536V0h6v3h6a4 4 0 0 1 3 1.355A4 4 0 0 1 47 3h8a4 4 0 0 1 2 .536V0h9zm95-4h6a4 4 0 0 1 4 4v14h-26a4 4 0 0 1-3-1.356V21h-8v-6h7V7a4 4 0 0 1 4-4h9c.351 0 .687.063 1 .174V0h6z"
                />
                <path
                  fill="#fff"
                  d="M13.5 5a2 2 0 0 1 2 2H9v2h4v2H9v8H3v-8H2V9h1V7a2 2 0 0 1 2-2z"
                />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M27 5a2 2 0 0 1 2 2v12H18a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h9V7H17a2 2 0 0 1 2-2zm-5 12h2v-5h-2z"
                  clipRule="evenodd"
                />
                <path
                  fill="#fff"
                  d="M33 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2zM55 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2zM64 17h2v2h-5a2 2 0 0 1-2-2V2h5z"
                />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M78 5a2 2 0 0 1 2 2v4h-7v6h2v-4h5v5a1 1 0 0 1-1 1H69a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2zm-5 4h2V7h-2z"
                  clipRule="evenodd"
                />
                <path
                  fill="#fff"
                  d="M84 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2zM106 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2z"
                />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M120 5a2 2 0 0 1 2 2v4h-7v6h2v-4h5v5a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2zm-5 4h2V7h-2z"
                  clipRule="evenodd"
                />
                <path
                  fill="#fff"
                  d="M134 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2zM140 17v2h-4v-2zM154 5a1 1 0 0 1 1 1v5h-5V7h-2v10h2v-4h5v5a1 1 0 0 1-1 1h-9a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM159 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2z"
                />
              </svg>
              <span className="sr-only">Startseite</span>
            </Link>
            {!showNavigation && (
              <div>
                <Button
                  version="gray-transparent"
                  size="icon"
                  icon={<MagnifyingGlassIcon weight="bold" />}
                  aria-label="Suchen"
                  onClick={openSearch}
                />
                <Button
                  version="gray-transparent"
                  size="icon"
                  icon={<ListIcon weight="bold" />}
                  onClick={toggleNavigation}
                  aria-expanded={false}
                />
              </div>
            )}
            {showNavigation && (
              <Button
                version="gray-transparent"
                size="icon"
                icon={<XIcon weight="bold" />}
                onClick={toggleNavigation}
                aria-expanded={false}
              />
            )}
          </div>
        )}
        {!!selectedSubNavigation && (
          <div className="flex h-15 items-center justify-between px-2">
            <Button
              version="gray-transparent"
              size="icon"
              icon={<CaretLeftIcon weight="bold" />}
              onClick={() => setSelectedSubNavigation(null)}
            />
            <p className="text-lg font-black">
              {selectedSubNavigation.page.title}
            </p>
            <Button
              version="gray-transparent"
              size="icon"
              icon={<XIcon weight="bold" />}
              onClick={toggleNavigation}
              aria-expanded={false}
            />
          </div>
        )}
      </div>
      <div
        aria-hidden={!showNavigation}
        className={clsx(
          'relative flex flex-1 flex-col overflow-x-hidden overflow-y-auto',
          {
            hidden: !showNavigation,
          }
        )}
      >
        <Transition
          as="div"
          show={selectedSubNavigation === null}
          enter="transition ease-in-out duration-150 transform"
          enterFrom="opacity-0 -translate-x-full"
          enterTo="opacity-100 translate-x-0"
          leave="transition ease-in-out duration-150 transform absolute top-0 left-0"
          leaveFrom="opacity-100 translate-x-0"
          leaveTo="opacity-0 -translate-x-full"
          className="flex h-full flex-col justify-between"
        >
          <ul className="mt-5">
            {mainNavigation.map((item) => (
              <Fragment key={item.page.title}>
                {item.children.length === 0 && item.page.url && (
                  <li>
                    <Link
                      href={item.page.url}
                      className="block px-5 py-2 text-xl font-bold"
                    >
                      {item.page.title}
                    </Link>
                  </li>
                )}
                {item.children.length > 0 && (
                  <li>
                    <button
                      onClick={() => setSelectedSubNavigation(item)}
                      className="flex w-full items-center justify-between px-5 py-2 text-xl font-bold"
                    >
                      <span>{item.page.title}</span>
                      <CaretRightIcon
                        weight="bold"
                        size="20"
                        className="ml-1"
                      />
                    </button>
                  </li>
                )}
              </Fragment>
            ))}
          </ul>
        </Transition>
        <Transition
          as="div"
          className="flex flex-1 flex-col"
          show={selectedSubNavigation !== null}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="opacity-0 translate-x-full"
          enterTo="opacity-100 translate-x-0"
          leave="transition ease-in-out duration-300 transform absolute top-0 left-0"
          leaveFrom="opacity-100 translate-x-0"
          leaveTo="opacity-0 transform-x-full"
        >
          {selectedSubNavigation && (
            <ul>
              {selectedSubNavigation.children.map((child, childIndex) => (
                <div
                  key={childIndex}
                  className={clsx('group shrink-0 py-6', {
                    'is-blue bg-linear-to-b from-blue-600 to-blue-700 text-white':
                      childIndex === 1,
                  })}
                >
                  {child.page.url && (
                    <Link href={child.page.url} className="px-5 py-2 font-bold">
                      {child.page.title}
                    </Link>
                  )}
                  <div
                    className={clsx({
                      'space-y-3': child.children.every((c) => !c.page.url),
                    })}
                  >
                    {child.children.map((subChild) => (
                      <Fragment key={subChild.page.title}>
                        {subChild.page.url &&
                          subChild.children.length === 0 && (
                            <Link
                              href={subChild.page.url}
                              className="flex items-center justify-between px-5 py-2 text-lg"
                            >
                              <span>{subChild.page.title}</span>
                              <CaretRightIcon
                                weight="bold"
                                size="20"
                                className="ml-1"
                              />
                            </Link>
                          )}
                        {subChild.children.length > 0 && (
                          <div>
                            <p className="mx-5 text-sm font-bold text-gray-600 group-[.is-blue]:text-white/80">
                              {subChild.page.title}
                            </p>
                            <ul className="mt-4">
                              {subChild.children.map(
                                (subSubChild, subSubChildIndex) => (
                                  <li key={subSubChildIndex}>
                                    {subSubChild.page.url && (
                                      <Link
                                        href={subSubChild.page.url}
                                        className="flex items-center justify-between px-5 py-2 text-lg"
                                      >
                                        <span>{subSubChild.page.title}</span>
                                        <CaretRightIcon
                                          weight="bold"
                                          size="20"
                                          className="ml-1"
                                        />
                                      </Link>
                                    )}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                  {selectedNavIndex === 0 && childIndex === 0 && (
                    <button
                      onClick={() => {
                        openSearch()
                      }}
                      className="mt-4 flex w-full items-center gap-2 px-5 py-2 text-lg font-bold text-blue-700"
                    >
                      <MagnifyingGlassIcon
                        weight="bold"
                        size={20}
                        className="shrink-0"
                      />
                      <span>Standort suchen</span>
                    </button>
                  )}
                </div>
              ))}
            </ul>
          )}
        </Transition>
      </div>
    </div>
  )
}
